import React, { useContext, useState } from 'react'

import { Box, ButtonV2, Div, LazyBackgroundImage, Row } from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'

import StaticImageMap from '@/components/pages/production/components/static-image-map'
import ListingsContext from '@/context/listings'
import { buildResponsiveHighlightedMapImageUrls } from '@/context/listings/utils'

import styles from './styles.module.scss'

const MapSeatView: React.FC<{
    onBackButtonClick: () => void
    sectionId?: string
    jsonFileName?: string | null
    staticMapUrl?: string | null
    seatView?: string | null
}> = ({ onBackButtonClick, sectionId, jsonFileName, staticMapUrl, seatView }) => {
    const [toggle, setToggle] = useState(seatView ? 'SeatView' : 'Map')

    const {
        selectors: { globalDataSelector },
    } = useContext(ListingsContext)

    if (!sectionId) return null

    if (!seatView && !jsonFileName && !staticMapUrl) {
        return (
            <Row className={styles.noInteractiveMap}>
                <ButtonV2
                    className={styles.backButton}
                    importance="text"
                    data-testid="seat-view-back-button"
                    onClick={onBackButtonClick}
                    icon="back"
                    iconSize="md"
                />
            </Row>
        )
    }

    const isSeatViewThumbnail = toggle === 'Map'
    const imageStyle = classNames(styles.image, { [styles.thumbnail]: isSeatViewThumbnail })

    const isMapThumbnail = toggle === 'SeatView' && seatView
    const mapStyle = classNames(styles.map, { [styles.thumbnail]: isMapThumbnail })

    // needed to make the map re-render, allowing seatView and mapView toggle
    const mapImageStyle = classNames(styles.mapImage, { [styles.mapThumbnail]: isMapThumbnail })

    const hasInteractiveMap = !!jsonFileName

    const listingRowMapUrl = buildResponsiveHighlightedMapImageUrls({
        layoutId: globalDataSelector?.[0].layoutId || '',
        sectionId,
    })

    return (
        <>
            <Box mobileDisplay={'block'} tabletDisplay={'block'} desktopDisplay={'block'} fullHeight>
                <Row>
                    <Row className={styles.backContainer}>
                        <ButtonV2
                            className={styles.backButton}
                            importance="secondary"
                            icon={'back'}
                            withIcon={true}
                            data-testid="seat-view-back-button"
                            onClick={onBackButtonClick}
                        />
                    </Row>
                    <Row
                        className={styles.heroContainer}
                        onClick={(e: any) => {
                            const target = e.target as HTMLElement
                            if (
                                target.classList.contains(styles.thumbnail) ||
                                target.classList.contains(styles.mapThumbnail)
                            )
                                setToggle(toggle === 'SeatView' ? 'Map' : 'SeatView')
                        }}
                    >
                        {seatView && (
                            <>
                                <Box mobileDisplay="none" tabletDisplay="none" desktopDisplay="block" fullHeight>
                                    <LazyBackgroundImage
                                        url={seatView}
                                        className={imageStyle}
                                        data-testid="seat-view-image"
                                    />
                                </Box>
                                <Box
                                    mobileDisplay="block"
                                    tabletDisplay="block"
                                    desktopDisplay="block"
                                    fullHeight
                                    mobileFlexGrow={true}
                                    tabletFlexGrow={true}
                                >
                                    <LazyBackgroundImage
                                        url={seatView}
                                        className={imageStyle}
                                        data-testid={isSeatViewThumbnail ? 'seat-view-thumbnail' : 'seat-view-image'}
                                    />
                                </Box>
                            </>
                        )}

                        <Div
                            mobileDisplay={'flex'}
                            tabletDisplay={'flex'}
                            desktopDisplay={'flex'}
                            className={mapStyle}
                            data-testid={isMapThumbnail ? 'map-thumbnail' : 'map-container'}
                        >
                            {hasInteractiveMap ? (
                                <LazyBackgroundImage
                                    url={listingRowMapUrl}
                                    className={mapImageStyle}
                                    data-testid="map-image"
                                />
                            ) : (
                                <StaticImageMap />
                            )}
                        </Div>
                    </Row>
                </Row>
            </Box>
            {!hasInteractiveMap && (
                <Box mobileDisplay={'none'} tabletDisplay={'none'} desktopDisplay={'block'}>
                    <Div
                        mobileDisplay={'none'}
                        tabletDisplay={'none'}
                        desktopDisplay={'flex'}
                        className={styles.noInteractiveMap}
                    >
                        <ButtonV2
                            className={styles.backButton}
                            importance="text"
                            data-testid="seat-view-back-button"
                            onClick={onBackButtonClick}
                            icon="back"
                            iconSize="md"
                        />
                    </Div>
                </Box>
            )}
        </>
    )
}

export default MapSeatView
